/**
 * AiCallsList — renders the `backend_tasks/{id}/ai_calls/*` subcollection
 * (Section 7.6.4) inside the TaskDetailDialog.
 *
 * Each call is collapsed by default; clicking the header expands to show:
 *   - Input summary (topic, platform, model, aspect, masterId, ...)
 *   - Full system / user prompt sent to the model
 *   - Full raw response + parsed output + handler-normalised result
 *   - Token usage + duration
 *   - Parse failure reason / error (when status='error')
 *
 * Copy-to-clipboard buttons on each block. Monospace JSON / pre-wrap text
 * blocks for legibility.
 */

import React, { useState } from 'react';
import {
  Box, Stack, Typography, Chip, IconButton, Tooltip, Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTaskAiCalls, type AiCallDoc } from '../hooks/useTaskAiCalls';

export interface AiCallsListProps {
  projectId: string;
  taskId: string;
}

const formatDuration = (ms?: number): string => {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const formatTimestamp = (ts: AiCallDoc['startedAt']): string => {
  if (!ts) return '—';
  if (ts instanceof Date) return ts.toLocaleTimeString();
  if ('seconds' in ts && typeof ts.seconds === 'number') {
    return new Date(ts.seconds * 1000).toLocaleTimeString();
  }
  return '—';
};

const StatusChip: React.FC<{ status?: 'running' | 'completed' | 'error' }> = ({ status }) => {
  if (status === 'error') {
    return <Chip label="error" size="small" color="error" />;
  }
  if (status === 'running') {
    return <Chip label="running" size="small" color="primary" variant="outlined" />;
  }
  if (status === 'completed') {
    return <Chip label="completed" size="small" color="success" variant="outlined" />;
  }
  return <Chip label="—" size="small" />;
};

const CodeBlock: React.FC<{
  label: string;
  value: string | undefined;
  maxHeight?: number;
}> = ({ label, value, maxHeight = 320 }) => {
  if (!value) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Tooltip title="Copy">
          <IconButton
            size="small"
            sx={{ p: 0.25 }}
            onClick={() => navigator.clipboard?.writeText(value)}
          >
            <ContentCopyIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box
        sx={{
          p: 1, bgcolor: 'action.hover', borderRadius: 1,
          fontFamily: 'monospace', fontSize: '0.7rem',
          maxHeight, overflow: 'auto',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}
      >
        {value}
      </Box>
    </Box>
  );
};

const AiCallRow: React.FC<{ call: AiCallDoc }> = ({ call }) => {
  const [expanded, setExpanded] = useState(false);
  const inputSummary = call.inputSummary ?? {};
  const platform = inputSummary.platform as string | undefined;
  const aspect = inputSummary.aspectLabel as string | undefined;
  const tokensIn = call.capturedUsage?.inputTokens ?? call.patchUsage?.inputTokens;
  const tokensOut = call.capturedUsage?.outputTokens ?? call.patchUsage?.outputTokens;
  const hasPromptPayload = Boolean(call.systemPrompt || call.userMessage || call.fullPromptMarkdown);
  const hasOutputPayload = Boolean(call.rawResponse || call.parsed || call.result);
  const inputSummaryJson = (() => {
    try { return JSON.stringify(inputSummary, null, 2); } catch { return undefined; }
  })();

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          p: 1, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 1,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <IconButton size="small" sx={{ p: 0.25 }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <Stack direction="row" spacing={0.5} alignItems="center" flex={1} flexWrap="wrap">
          <Chip label={call.phase} size="small" color="primary" variant="outlined" />
          {call.handler && (
            <Typography variant="caption" color="text.secondary">
              {call.handler}
            </Typography>
          )}
          {platform && <Chip label={platform} size="small" />}
          {aspect && <Chip label={aspect} size="small" variant="outlined" />}
          <Chip
            label={hasPromptPayload ? 'prompt captured' : 'no prompt'}
            size="small"
            color={hasPromptPayload ? 'info' : 'warning'}
            variant="outlined"
          />
          <Chip
            label={hasOutputPayload ? 'output captured' : 'no output'}
            size="small"
            color={hasOutputPayload ? 'success' : 'warning'}
            variant="outlined"
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {formatDuration(call.durationMs)}
        </Typography>
        {(tokensIn != null || tokensOut != null) && (
          <Typography variant="caption" color="text.secondary">
            in:{tokensIn ?? 0} / out:{tokensOut ?? 0}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          {formatTimestamp(call.startedAt)}
        </Typography>
        <StatusChip status={call.status} />
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          {call.error && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="error.main">ERROR</Typography>
              <Typography variant="body2" color="error.main">{call.error}</Typography>
            </Box>
          )}
          {call.parseFailureReason && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="warning.main">PARSE FAILURE</Typography>
              <Typography variant="body2" color="warning.main">
                {call.parseFailureReason}
              </Typography>
            </Box>
          )}
          {call.capturedModel && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Model: {call.capturedModel}
            </Typography>
          )}
          {!hasPromptPayload && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
              Exact full prompt is not stored on this debug entry. For historical tasks, re-run
              after the backend prompt-capture update/restart. For non-text phases, the phase may
              have skipped the provider call.
            </Typography>
          )}
          {!hasOutputPayload && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
              Model output is not stored on this debug entry. Re-run after the backend
              debug-capture update/restart, or inspect the phase metadata if the provider call
              was intentionally skipped.
            </Typography>
          )}
          <CodeBlock
            label="Full prompt markdown (exact system + user message)"
            value={call.fullPromptMarkdown}
            maxHeight={720}
          />
          <CodeBlock label="System prompt" value={call.systemPrompt} maxHeight={520} />
          <CodeBlock label="User message" value={call.userMessage} maxHeight={520} />
          <CodeBlock label="Model output — raw AI response" value={call.rawResponse} maxHeight={520} />
          <CodeBlock label="Model output — parsed provider content" value={call.parsed} maxHeight={520} />
          <CodeBlock label="Model output — final handler result" value={call.result} maxHeight={520} />
          <CodeBlock label="Input summary" value={inputSummaryJson} maxHeight={200} />
        </Box>
      </Collapse>
    </Box>
  );
};

export const AiCallDebugBlocks: React.FC<{
  calls: AiCallDoc[];
  title?: string;
  emptyLabel?: string;
}> = ({ calls, title = `AI calls (${calls.length})`, emptyLabel }) => {
  if (calls.length === 0) {
    return emptyLabel ? (
      <Typography variant="caption" color="text.secondary">{emptyLabel}</Typography>
    ) : null;
  }
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {calls.map((c) => (
        <AiCallRow key={c.id} call={c} />
      ))}
    </Box>
  );
};

export const AiCallsList: React.FC<AiCallsListProps> = ({ projectId, taskId }) => {
  const { calls, loading, error } = useTaskAiCalls(projectId, taskId);

  if (loading && calls.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">Loading AI call history…</Typography>
    );
  }
  if (calls.length === 0 && !error) return null;
  if (error) {
    return (
      <Typography variant="caption" color="text.secondary">
        AI call history unavailable: {error.message}
      </Typography>
    );
  }

  return (
    <AiCallDebugBlocks calls={calls} />
  );
};

export default AiCallsList;
